import React, { useState } from 'react';
import {
  Box,
  Typography,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Button,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useLoans } from '../../hooks/loans/useLoans';
import { LoanStatus } from '../../api/types/loan.types';
import { format } from 'date-fns';

const ActiveLoans: React.FC = () => {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState<LoanStatus | undefined>(undefined);
  const { data: loans, isLoading, error } = useLoans({ status: statusFilter });

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const getStatusColor = (status: LoanStatus) => {
    switch (status) {
      case 'ACTIVE':
        return 'success';
      case 'PAID':
        return 'info';
      case 'OVERDUE':
        return 'warning';
      case 'DEFAULTED':
      case 'WRITTEN_OFF':
        return 'error';
      default:
        return 'default';
    }
  };

  if (error) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom fontWeight="bold">
          Active Loans
        </Typography>
        <Alert severity="error" sx={{ mt: 2 }}>
          Failed to load loans. {error instanceof Error ? error.message : 'Please try again.'}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          Active Loans
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Manage active loans and repayments
        </Typography>
      </Box>

      <Box sx={{ mb: 3, display: 'flex', gap: 1 }}>
        <Chip
          label="All"
          onClick={() => setStatusFilter(undefined)}
          color={!statusFilter ? 'primary' : 'default'}
          variant={!statusFilter ? 'filled' : 'outlined'}
        />
        <Chip
          label="Active"
          onClick={() => setStatusFilter('ACTIVE' as LoanStatus)}
          color={statusFilter === 'ACTIVE' ? 'success' : 'default'}
          variant={statusFilter === 'ACTIVE' ? 'filled' : 'outlined'}
        />
        <Chip
          label="Overdue"
          onClick={() => setStatusFilter('OVERDUE' as LoanStatus)}
          color={statusFilter === 'OVERDUE' ? 'warning' : 'default'}
          variant={statusFilter === 'OVERDUE' ? 'filled' : 'outlined'}
        />
        <Chip
          label="Paid"
          onClick={() => setStatusFilter('PAID' as LoanStatus)}
          color={statusFilter === 'PAID' ? 'info' : 'default'}
          variant={statusFilter === 'PAID' ? 'filled' : 'outlined'}
        />
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell><strong>Loan Number</strong></TableCell>
              <TableCell><strong>Customer</strong></TableCell>
              <TableCell><strong>Principal</strong></TableCell>
              <TableCell><strong>Outstanding</strong></TableCell>
              <TableCell><strong>Paid</strong></TableCell>
              <TableCell><strong>Due Date</strong></TableCell>
              <TableCell><strong>Status</strong></TableCell>
              <TableCell align="right"><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : loans?.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">No loans found</Typography>
                </TableCell>
              </TableRow>
            ) : (
              loans?.map((loan) => (
                <TableRow key={loan.loanId} hover>
                  <TableCell>{loan.loanNumber}</TableCell>
                  <TableCell>{loan.customerName}</TableCell>
                  <TableCell>{formatCurrency(loan.principalAmount)}</TableCell>
                  <TableCell>{formatCurrency(loan.amountOutstanding)}</TableCell>
                  <TableCell>{formatCurrency(loan.amountPaid)}</TableCell>
                  <TableCell>
                    {loan.dueDate ? format(new Date(loan.dueDate), 'MMM dd, yyyy') : '-'}
                    {loan.daysOverdue > 0 && (
                      <Typography variant="caption" color="error" display="block">
                        {loan.daysOverdue} days overdue
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={loan.loanStatus}
                      size="small"
                      color={getStatusColor(loan.loanStatus) as any} // eslint-disable-line @typescript-eslint/no-explicit-any
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => navigate(`/loans/${loan.loanId}`)}
                    >
                      View Details
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default ActiveLoans;
